/**
 * TypeScript mirror of the C# wire types in
 * src/lambda/shared/GoobDoobVtt.Domain/SceneMessages.cs.
 *
 * Keep in lockstep with that file. All inbound messages tolerate unknown
 * fields so adding new optional properties is backward compatible.
 */

import type { SceneElement } from "../../lib/scene";

// Client → server action strings.
export const SceneActions = {
  Subscribe: "scene/subscribe",
  Unsubscribe: "scene/unsubscribe",
  Op: "scene/op",
  Cursor: "scene/cursor",
  StrokeLive: "scene/stroke-live",
} as const;

// Server → client envelope `type` values.
export const SceneServerTypes = {
  SceneState: "scene/state",
  SceneOp: "scene/op",
  Cursor: "scene/cursor",
  StrokeLive: "scene/stroke-live",
  OpRejected: "scene/op-rejected",
  Error: "error",
  Ack: "ack",
} as const;

// -------------------- outbound (client → server) --------------------

export interface SubscribeRequest {
  action: "scene/subscribe";
  sessionId: string;
  mapId: string;
}

export interface OpRequest {
  action: "scene/op";
  mapId: string;
  element: SceneElement;
}

export interface CursorRequest {
  action: "scene/cursor";
  mapId: string;
  x: number;
  y: number;
}

export interface StrokeLiveRequest {
  action: "scene/stroke-live";
  mapId: string;
  elementId: string;
  points?: number[];
  pressures?: number[];
  strokeColor?: string;
  strokeWidth?: number;
}

// -------------------- inbound (server → client) --------------------

export interface ServerEnvelope<T = unknown> {
  type: string;
  payload?: T;
}

export interface SceneStatePayload {
  mapId: string;
  elements: SceneElement[];
}

/** Same shape as OpRequest but with an `origin` field added by the server. */
export interface SceneOpPayload {
  mapId: string;
  element: SceneElement;
  origin?: string;
}

export interface CursorPayload {
  mapId: string;
  userId: string;
  username?: string;
  x: number;
  y: number;
}

export interface StrokeLivePayload {
  mapId: string;
  elementId: string;
  userId: string;
  points?: number[];
  pressures?: number[];
  strokeColor?: string;
  strokeWidth?: number;
}

export interface OpRejectedPayload {
  elementId: string;
  reason: string;
  /** Current server-authoritative element (null on non-version rejections). */
  current: SceneElement | null;
}

export interface ErrorPayload {
  code: string;
  message: string;
}
